// Libraries
import {
  Component,
  OnInit
} from '@angular/core';
import { FormBuilder, FormGroup, AbstractControl } from '@angular/forms';
import * as lodash from 'lodash';

// Models
import { FormError } from './form-error.interface';

// Services

/**
 * `GenericFormComponent` implements a component to manage generic forms and errors.
 *
 * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
 * @export
 * @abstract
 */
@Component({
  selector: 'wp-generic-form',
  template: ``
})
export abstract class GenericFormComponent<T> implements OnInit {
  // Public properties
  /**
   * Current form object.
   */
  form: FormGroup;
  /**
   * Flag to maintain if form has been submitted or not.
   */
  submitted = false;

  // Private properties

  // Public methods
  /**
   * Creates an instance of GenericFormComponent.
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  constructor(
    protected fb: FormBuilder
  ) {
  }

  /**
   * Implements on init method.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  ngOnInit() {
    // Build form
    this.buildForm();
  }

  /**
   * Handles submit event.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  onSubmit() {
    // Toggle flag
    this.submitted = true;
    // Exit if form is invalid
    if (this.form.invalid) {
      return;
    }
    // Submit form
    this.submitForm();
  }

  /**
   * Get current form errors.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  formErrors(): FormError[] | undefined {
    if (this.submitted && this.form.errors) {
      return this.getErrors(this.form);
    }
  }

  /**
   * Get current field errors.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  fieldErrors(name: string): FormError[] | undefined {
    let control = this.findFieldControl(name);
    if (control && (control.touched || this.submitted) && control.errors) {
      return this.getErrors(control);
    } else {
      return undefined;
    }
  }

  /**
   * Reset field errors.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  resetFieldErrors(name: string) {
    const control = this.form.get(name);
    // Check if control is set
    if (control) {
      control.setErrors(null);
    }
  }

  // Protected methods
  /**
   * Create form group and controls.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @protected
   * @abstract
   * @memberof GenericFormComponent
   */
  protected abstract buildForm(): void;

  /**
   * Handles submit event if form are valid.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @protected
   * @abstract
   * @memberof GenericFormComponent
   */
  protected abstract submitForm(): void;

  /**
   * Handles submit success event.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @protected
   * @abstract
   * @memberof GenericFormComponent
   */
  protected abstract handleSubmitSuccess(response: T): void;

  /**
   * Handles submit error event.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @protected
   * @memberof GenericFormComponent
   */
  protected handleSubmitError(response: any) {
    // Check if is unprocessable entity
    if (response.status === 422) {
      const globalErrors = response.error && response.error.errors ? response.error.errors : {};
      const fields = Object.keys(globalErrors || {});
      // Find and set all field errors
      fields.forEach((field) => {
        const fieldKey = lodash.map(field.split('.'), function(key) { return lodash.camelCase(key); }).join('.');
        const control = this.findFieldControl(field);
        const errors = this.fetchFieldErrors(globalErrors, field);
        // Set errors to control
        if (control) {
          control.setErrors(errors);
        }
      });
    }
  }

  /**
   * Get current errors from form control.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @protected
   * @memberof GenericFormComponent
   */
  protected getErrors(control: AbstractControl): FormError[] | undefined {
    // Check if erros are not set, return null value
    if (!control.errors) {
       return undefined;
    }
    return Object.keys(control.errors)
      .filter((error) => control.errors ? control.errors[error] : '')
      .map((error) => {
        return {
          error: error,
          params: {}
        };
      });
  }

  /**
   * Find field control on form object.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @protected
   * @memberof GenericFormComponent
   */
  protected findFieldControl(field: string): AbstractControl | null {
    let control: AbstractControl | null = this.form;
    if (field === 'base') {
      control = this.form;
    } else if (this.form.contains(field)) {
      control = this.form.get(field);
    } else if (field.match(/_id$/) && this.form.contains(field.substring(0, field.length - 3))) {
      control = this.form.get(field.substring(0, field.length - 3));
    } else if (field.indexOf('.') > 0) {
      let group = this.form;
      field.split('.').forEach((f) => {
        if (group.contains(f)) {
          control = group.get(f);
          if (control instanceof FormGroup) {
            group = control;
          }
        } else {
          control = group;
        }
      });
    }
    return control;
  }

  /**
   * Parse backend errors.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof GenericFormComponent
   */
  private fetchFieldErrors(data: any, field: string): any {
    const errors: any = {};
    const errorsKeys = Object.keys(data[field]);
    errorsKeys.forEach((errorKey: string) => {
      errors[errorKey] = true;
    });
    return errors;
  }
}
